// src/components/StudyModeModal.tsx
// ✅ CORRIGIDO: Bug do loading invertido + re-trigger desnecessário do useEffect

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Timer, RefreshCcw, X, Lock } from 'lucide-react';
import './StudyModeModal.css';

interface StudyModeModalProps {
    isOpen: boolean;
    onClose: () => void;
    deckId: string;
    subdeckId: string;
    deckName: string;
    subdeckName: string;
    isCustomDeck?: boolean;
    prefetchedStats?: { reviewCount: number };
    isPurchasedDeck?: boolean;
}

interface ModeStats {
    reviewCount: number;
}

const StudyModeModal: React.FC<StudyModeModalProps> = ({
    isOpen,
    onClose,
    deckId,
    subdeckId,
    deckName,
    subdeckName,
    isCustomDeck = false,
    prefetchedStats,
    isPurchasedDeck = true
}) => {
    const navigate = useNavigate();

    // ✅ FIX: se prefetchedStats existe, começa sem loading (false)
    // se não existe, começa com loading (true) para buscar
    const [modeStats, setModeStats] = useState<ModeStats>(
        prefetchedStats ?? { reviewCount: 0 }
    );
    const [loading] = useState<boolean>(false);

    const loadModeStats = useCallback(async () => {
        try {
            // ← SEM setLoading(true) aqui
            const token = localStorage.getItem('accessToken');
            const statsUrl = isCustomDeck
                ? `/api/progress/custom-mode-stats/${deckId}/${subdeckId}`
                : `/api/progress/mode-stats/${deckId}/${subdeckId}`;

            const response = await fetch(statsUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setModeStats(data);
            }
        } catch (error) {
            console.error('Erro ao carregar stats de modo:', error);
        }
        // ← SEM finally/setLoading
    }, [isCustomDeck, deckId, subdeckId]);

    useEffect(() => {
        if (!isOpen) return;
        if (prefetchedStats) {
            setModeStats(prefetchedStats);
            return;
        }
        loadModeStats();
    }, [isOpen, loadModeStats, prefetchedStats]);


    const handleModeSelect = (mode: 'free' | 'focus' | 'review') => {
        if (mode === 'review' && modeStats.reviewCount === 0) {
            return;
        }

        const savedContext = sessionStorage.getItem('subdecksContext');
        const context = savedContext ? JSON.parse(savedContext) : {};

        const studyPath = isCustomDeck
            ? `/custom-decks/${deckId}/study/${subdeckId}?mode=${mode}`
            : `/study/${deckId}/${subdeckId}?mode=${mode}`;

        navigate(studyPath, { state: context });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="study-mode-overlay" onClick={onClose}>
            <div className="study-mode-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="study-mode-header">
                    <div className="smm-header-center">
                        <div className="study-mode-header-logo">GRAIA</div>
                        <div className="header-text">
                            <h2>Escolha o modo de estudo</h2>
                            <p className="study-mode-subtitle">
                                {deckName} → {subdeckName}
                            </p>
                        </div>
                    </div>
                    <div className="smm-header-right">
                        <img src="/graia-logo-azul.png" alt="GRAIA" className="smm-logo-img" />
                        <button className="study-mode-close" onClick={onClose} aria-label="Fechar">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Loading */}
                {loading ? (
                    <div className="study-mode-loading">
                        <div className="spinner"></div>
                        <p>Carregando modos</p>
                    </div>
                ) : (
                    <>
                        {/* Cards de Modo */}
                        <div className="study-mode-cards">
                            {/* MODO LIVRE */}
                            <div
                                className="study-mode-card"
                                onClick={() => handleModeSelect('free')}
                            >
                                <div className="mode-icon-wrapper">
                                    <Layers className="mode-icon" size={24} />
                                </div>
                                <h3>Modo Livre</h3>
                                <p className="mode-description">
                                    Estudo completo, sem restrições
                                </p>
                                <ul className="mode-features">
                                    <li>Navegação completa</li>
                                    <li>Sem limite de tempo</li>
                                    <li>Estatísticas registradas</li>
                                </ul>
                                <button className="mode-start-btn">
                                    Iniciar estudo
                                </button>
                            </div>

                            {/* MODO FOCO */}
                            <div
                                className={`study-mode-card ${!isPurchasedDeck ? 'locked' : ''}`}
                                onClick={() => isPurchasedDeck && handleModeSelect('focus')}
                            >
                                <div className="mode-icon-wrapper">
                                    <Timer className="mode-icon" size={24} />
                                </div>
                                <h3>Modo Foco</h3>
                                <div className="mode-badge">20 minutos</div>
                                <p className="mode-description">
                                    Sessão cronometrada, sem distrações
                                </p>
                                <ul className="mode-features">
                                    <li>Fullscreen automático</li>
                                    <li>Timer de 20 minutos</li>
                                    <li>Sessão ininterrupta</li>
                                </ul>
                                {!isPurchasedDeck ? (
                                    <div className="mode-locked-badge">
                                        <Lock size={14} /> Requer plano completo
                                    </div>
                                ) : (
                                    <button className="mode-start-btn">
                                        Iniciar foco
                                    </button>
                                )}
                            </div>

                            {/* MODO REVISÃO */}
                            <div
                                className={`study-mode-card ${(!isPurchasedDeck || modeStats.reviewCount === 0) ? 'disabled' : ''} ${!isPurchasedDeck ? 'locked' : ''}`}
                                onClick={() => isPurchasedDeck && modeStats.reviewCount > 0 && handleModeSelect('review')}
                            >
                                <div className="mode-icon-wrapper">
                                    <RefreshCcw className="mode-icon" size={24} />
                                </div>
                                <h3>Modo Revisão</h3>
                                <p className="mode-description">
                                    Apenas cards com nota baixa
                                </p>

                                {!isPurchasedDeck ? (
                                    <>
                                        <div className="mode-empty-state">
                                            <p>Disponível no plano completo</p>
                                            <span className="empty-hint">
                                                Adquira um plano para desbloquear todos os modos
                                            </span>
                                        </div>
                                        <div className="mode-locked-badge">
                                            <Lock size={14} /> Requer plano completo
                                        </div>
                                    </>
                                ) : modeStats.reviewCount > 0 ? (
                                    <>
                                        <div className="mode-review-count">
                                            {modeStats.reviewCount} {modeStats.reviewCount === 1 ? 'card' : 'cards'} para revisar
                                        </div>
                                        <ul className="mode-features">
                                            <li>Notas 1 e 2 apenas</li>
                                            <li>Foco em dificuldades</li>
                                            <li>Remoção automática</li>
                                        </ul>
                                        <button className="mode-start-btn">
                                            Iniciar revisão
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="mode-empty-state">
                                            <p>Nenhum card disponível</p>
                                            <span className="empty-hint">
                                                Estude primeiro para marcar cards difíceis
                                            </span>
                                        </div>
                                        <button className="mode-start-btn" disabled>
                                            Sem cards
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StudyModeModal;